import { useId } from '@react-aria/utils'
import React from 'react'
import { useI18nContext } from '../_hooks/I18n'
import {
  StyledCircle,
  StyledSpinnerContainer,
  StyledSpinnerContainerWithLabel,
  StyledSpinnerOverlay,
  StyledSpinnerOverlayContainer,
  StyledSpinnerOverlayLabel,
  StyledSpinnerSVG,
} from './Spinner.styles'
import type { SpinnerProps, SpinnerSize } from './Spinner.types'

const radiusMap: Record<SpinnerSize, number> = {
  xs: 18.5,
  sm: 18,
  md: 20,
  lg: 20.5,
}

const smallSizes: SpinnerSize[] = ['xs', 'sm']

export const defaultSpinnerSize: SpinnerSize = 'lg'

const Circle = React.forwardRef<
  HTMLDivElement,
  SpinnerProps & {
    mountTime: number
    labelId: string
    ariaHidden?: boolean
  }
>(function Circle(
  {
    className = '',
    color,
    loading = false,
    mountTime,
    size = defaultSpinnerSize,
    variant = 'default',
    label,
    labelId,
    ariaHidden = false,
  },
  ref
) {
  const i18n = useI18nContext()
  return (
    <StyledSpinnerContainer
      aria-labelledby={labelId}
      className={className}
      $color={color}
      $size={size}
      $variant={variant}
      ref={ref}
      role="progressbar"
    >
      <StyledSpinnerSVG
        aria-hidden={ariaHidden || undefined}
        aria-label={
          label ||
          i18n.t('core.spinner.loading', {
            defaultValue: 'Loading',
          })
        }
        id={ariaHidden ? undefined : labelId}
        role="img"
        viewBox="0 0 48 48"
        xmlns="http://www.w3.org/2000/svg"
        $animate={loading}
        $mountTime={mountTime}
      >
        <StyledCircle
          cx="24"
          cy="24"
          r={radiusMap[size]}
          $animate={loading}
          $mountTime={mountTime}
        />
      </StyledSpinnerSVG>
    </StyledSpinnerContainer>
  )
})

/**

 Spinners indicate a loading state on either a full page or smaller component,
 like a menu, select or button.

 @since 10.19.0

 @see [Storybook](https://stories.core.procore.com/?path=/story/core-react_demos-spinner--demo)

 @see [Design Guidelines](https://design.procore.com/spinner)

 */
export const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  function Spinner(props, ref) {
    const {
      children,
      className = '',
      color,
      label,
      loading = false,
      size = defaultSpinnerSize,
      variant = 'default',
      centered = false,
      ...rest
    } = props
    const labelId = useId()
    const mountTime = React.useRef(Date.now())
    const hasVisibleLabel = Boolean(
      children && label && !smallSizes.includes(size)
    )

    if (!children) {
      // We are preserving the current behavior of the Spinner component,
      // where the loading prop does not apply when no children are passed.
      return (
        <Circle
          {...props}
          labelId={labelId}
          loading
          mountTime={mountTime.current}
          ref={ref}
        />
      )
    }

    return (
      <StyledSpinnerOverlayContainer
        $loading={loading}
        className={className}
        $centered={centered}
        {...rest}
      >
        <StyledSpinnerOverlay />
        <StyledSpinnerContainerWithLabel aria-hidden={!loading}>
          <Circle
            {...props}
            ariaHidden={hasVisibleLabel}
            className=""
            labelId={labelId}
            mountTime={mountTime.current}
            ref={ref}
          />
          {!smallSizes.includes(size) && (
            <StyledSpinnerOverlayLabel
              id={hasVisibleLabel ? labelId : undefined}
            >
              {label}
            </StyledSpinnerOverlayLabel>
          )}
        </StyledSpinnerContainerWithLabel>
        {children}
      </StyledSpinnerOverlayContainer>
    )
  }
)

Spinner.displayName = 'Spinner'
